import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './App.css';
import Dashboard from './components/Dashboard';
import ClientList from './components/ClientList';
import Tutorial from './components/Tutorial';
import CuentaBloqueada from './components/CuentaBloqueada';
import { sincronizar, leerCola } from './utils/offline';
import AvisoDeVersion from './components/AvisoDeVersion';
import DeudaCliente from './components/DeudaCliente';
import AvisoDeMora from './components/AvisoDeMora';
import NewLoan from './components/NewLoan';
import Settings from './components/Settings';
import Payments from './components/Payments';
import Delinquents from './components/Delinquents';
import PorCobrar from './components/PorCobrar';
import EstadosDeCuenta from './components/EstadosDeCuenta';
import Papelera from './components/Papelera';
import Acuerdos from './components/Acuerdos';
import Informes from './components/Informes';
import Simulador from './components/Simulador';
import Importar from './components/Importar';
import Expenses from './components/Expenses';
import PublicProfiles from './components/PublicProfiles';
import NotificationManager from './components/NotificationManager';
import AvisoDiario from './components/AvisoDiario';
import OverdueLoans from './components/OverdueLoans';
import GainsReport from './components/GainsReport';
import AdminEmpresas from './components/AdminEmpresas';
import LoginRegister from './pages/LoginRegister';
import CuentaPendiente from './pages/CuentaPendiente';

// Los grupos de la barra. El orden es el del día de trabajo: primero lo que se
// toca a diario, después lo de prestar, y al final lo que se mira una vez al
// mes. Cambiar el orden es mover un renglón de aquí.
const GRUPOS = [
  { titulo: 'Lo de todos los días', botones: [
    ['dashboard', '🏠 Inicio'],
    ['payments', '💳 Pagos'],
    ['por-cobrar', '📅 Por cobrar'],
    ['deuda', '🧮 Deuda'],
    ['estados', '💬 Estados de cuenta'],
  ] },
  { titulo: 'Clientes y préstamos', botones: [
    ['clients', '👥 Clientes'],
    ['new-loan', '➕ Prestar'],
    ['simulador', '📐 Simular'],
    ['delinquents', '⚠️ Morosos'],
    ['overdue-loans', '📋 Vencimientos'],
    ['acuerdos', '🤝 Acuerdos'],
  ] },
  { titulo: 'El negocio', botones: [
    ['gains', '💰 Ganancias'],
    ['expenses', '💸 Gastos'],
    ['informes', '📈 Informes'],
    ['public-profiles', '👁️ Reputación'],
  ] },
];

// Lo que se abre de vez en cuando. Fuera de la barra, en un menú: la papelera
// se usa dos veces al mes y estaba ocupando primera fila.
const AJUSTES = [
  ['settings', '⚙️ Configuración'],
  ['importar', '📂 Pasar mi cartera'],
  ['notifications', '🔔 Notificaciones'],
  ['papelera', '🗑️ Papelera'],
  ['guia', '❓ Guía'],
];

function App() {
  // La primera vez que alguien entra ve la guía, no el panel vacío. Quien
  // acaba de comprar el programa se encontraba quince botones sin explicación,
  // teniendo esa pantalla escrita y sin conectar.
  //
  // La marca va por usuario: si mañana entra un socio con su propia cuenta,
  // también le toca su recorrido.
  const marcaDeGuia = () => {
    try {
      const usuario = JSON.parse(localStorage.getItem('user') || '{}');
      return usuario.id ? `guia-vista-${usuario.id}` : null;
    } catch (e) {
      return null;
    }
  };

  const esLaPrimeraVez = () => {
    const marca = marcaDeGuia();
    return Boolean(marca) && !localStorage.getItem(marca);
  };

  // El inicializador solo lee. Escribir aquí no funcionaba: React lo ejecuta
  // dos veces al montar y la segunda ya encontraba su propia marca, así que la
  // guía no llegaba a mostrarse nunca. Lo que escribe va en el efecto de abajo.
  const [page, setPage] = useState(() => (esLaPrimeraVez() ? 'guia' : 'dashboard'));

  const [clients, setClients] = useState([]);
  const [dashboard, setDashboard] = useState({});
  const [settings, setSettings] = useState({});
  const [user, setUser] = useState(null);
  // Estado de la suscripción cuando el servidor la rechaza: 'pendiente',
  // 'vencida' o 'suspendida'.
  const [cuentaBloqueada, setCuentaBloqueada] = useState(null);
  // Se enciende cuando el servidor rechaza el token por vencido, para poder
  // decirle POR QUÉ está viendo la pantalla de entrar otra vez.
  const [sesionVencida, setSesionVencida] = useState(false);
  const [menuAbierto, setMenuAbierto] = useState(false);

  // Marca la guía como vista. Va aquí y no en el inicializador de arriba
  // porque necesita `user`, y porque un inicializador no debe escribir nada:
  // React lo ejecuta dos veces al montar y la segunda ya encontraba su propia
  // marca, así que la guía no llegaba a mostrarse.
  useEffect(() => {
    const marca = marcaDeGuia();
    if (marca) localStorage.setItem(marca, '1');
  }, [user]);
  const [token, setToken] = useState(null);
  // Estado de la suscripción. Se consulta al servidor en cada carga: si se
  // fiara del token, suspender una cuenta no tendría efecto hasta que caducara.
  const [cuenta, setCuenta] = useState(null);

  useEffect(() => {
    // Cargar token y usuario del localStorage
    const savedToken = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (savedToken && savedUser) {
      setToken(savedToken);
      setUser(JSON.parse(savedUser));
      // Configurar header de autorización para axios
      axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
    }
  }, []);

  useEffect(() => {
    if (!localStorage.getItem('token')) return;
    fetchCuenta();
    fetchDashboard();
    fetchClients();
    fetchSettings();
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await axios.get('/api/dashboard');
      setDashboard(res.data);
    } catch (err) {
      console.error('Error fetching dashboard:', err);
    }
  };

  const fetchClients = async () => {
    try {
      const res = await axios.get('/api/clients');
      setClients(res.data);
    } catch (err) {
      console.error('Error fetching clients:', err);
    }
  };

  const fetchCuenta = async () => {
    try {
      const res = await axios.get('/api/mi-cuenta');
      setCuenta(res.data);
    } catch (err) {
      console.error('Error consultando la cuenta:', err);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get('/api/settings');
      setSettings(res.data);
    } catch (err) {
      console.error('Error fetching settings:', err);
    }
  };

  // Cerrar la sesión. Va declarado ANTES del interceptor porque este lo nombra
  // en su lista de dependencias, y esa lista se evalúa durante el render: con
  // la declaración más abajo, la constante todavía no existe y revienta.
  const cerrarSesion = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setCuenta(null);
    setCuentaBloqueada(null);
    setPage('dashboard');
  }, []);

  // El servidor responde 402 en todas las rutas cuando la suscripción no está
  // activa. Sin esto, la aplicación se limitaba a escribirlo en la consola y el
  // usuario veía un panel en ceros sin ninguna explicación.
  //
  // Y responde 401 cuando la sesión se venció —el token dura siete días—. Pasaba
  // lo mismo: la aplicación seguía en pie mostrando ceros. Un cliente con 177
  // clientes registrados vio «0» y se puso a crearlos otra vez a mano; solo no
  // los duplicó porque esas peticiones también salían 401.
  //
  // Ver ceros no es un error visible: parece que se borraron los datos. Por eso
  // se cierra la sesión y se manda a entrar de nuevo, que es lo único que el
  // usuario puede hacer al respecto.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (respuesta) => respuesta,
      (error) => {
        const estado = error.response?.status;
        if (estado === 402) {
          setCuentaBloqueada(error.response.data?.estado || 'pendiente');
        }
        // El propio login devuelve 401 con la contraseña equivocada: ahí no hay
        // sesión que cerrar y el mensaje lo da la pantalla de entrar.
        if (estado === 401 && !String(error.config?.url || '').includes('login')) {
          setSesionVencida(true);
          cerrarSesion();
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, [cerrarSesion]);

  // Sube los cobros anotados sin señal. Se intenta al arrancar y cada vez que
  // vuelve la conexión, desde cualquier pantalla: quien cobró en la calle no
  // tiene por qué volver a Pagos para que se envíen.
  useEffect(() => {
    if (!user || !token) return;

    const enviar = async () => {
      if (leerCola().length === 0) return;

      const r = await sincronizar(axios);

      if (r.rechazado) {
        alert(
          `⚠️ Un cobro guardado sin señal no se pudo registrar:\n\n` +
          `${r.rechazado.motivo}\n\n` +
          `Anótalo de nuevo a mano. Los demás sí se enviaron.`
        );
      }
      if (r.enviados > 0) {
        fetchDashboard();
        fetchClients();
      }
    };

    enviar();
    window.addEventListener('online', enviar);
    return () => window.removeEventListener('online', enviar);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  const handleLogin = (userData, newToken) => {
    setUser(userData);
    setToken(newToken);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('token', newToken);

    // La guía también al entrar, no solo al recargar con la sesión ya abierta.
    // El estado inicial de la página se calcula al montar la aplicación, cuando
    // todavía no hay usuario, así que quien acababa de registrarse —justo para
    // quien se hizo— nunca la veía.
    const marca = userData.id ? `guia-vista-${userData.id}` : null;
    if (marca && !localStorage.getItem(marca)) setPage('guia');
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    fetchCuenta();
    fetchDashboard();
    fetchClients();
    fetchSettings();
  };

  // Salir a propósito, desde el botón. Limpia además el aviso de sesión
  // vencida: si se sale uno mismo, no hay nada que explicar al volver.
  const handleLogout = () => {
    setSesionVencida(false);
    cerrarSesion();
  };

  // Mostrar login si no hay usuario
  if (!user || !token) {
    return <LoginRegister onLogin={handleLogin} sesionVencida={sesionVencida} />;
  }

  // Con la suscripción sin activar no tiene sentido enseñar el programa vacío
  if (cuentaBloqueada) {
    return (
      <CuentaBloqueada
        estado={cuentaBloqueada}
        email={user.email}
        onSalir={handleLogout}
      />
    );
  }

  // Cuenta sin activar, vencida o suspendida: no se entra a la aplicación.
  if (cuenta && cuenta.estado !== 'activa') {
    return (
      <CuentaPendiente
        estado={cuenta.estado}
        empresa={cuenta.empresa}
        diasProrroga={cuenta.dias_prorroga}
        onLogout={handleLogout}
      />
    );
  }

  // 🏢 Empresas solo la ve quien vende el programa.
  const ajustesVisibles = (cuenta && cuenta.es_admin)
    ? [...AJUSTES, ['admin', '🏢 Empresas']]
    : AJUSTES;
  const enAjustes = ajustesVisibles.some(([destino]) => destino === page);

  return (
    <div className="App">
      <header className="header">
        <h1>💎 FlexFlow Pro - by {user.companyName || user.email}</h1>
        {/* La barra, por grupos. Antes eran dieciocho botones sueltos, todos
            del mismo tamaño: lo que se toca veinte veces al día al lado de lo
            que se abre una vez al año. Agrupados, la primera fila basta para
            trabajar un día entero.

            Se arma de una lista y no a mano botón por botón: reordenarla es
            mover un renglón, y así no vuelve a crecer sin que se note. */}
        <nav className="nav-grupos">
          {GRUPOS.map(grupo => (
            <div key={grupo.titulo}>
              <div className="nav-grupo-titulo">{grupo.titulo}</div>
              <div className="nav">
                {grupo.botones.map(([destino, rotulo]) => (
                  <button
                    key={destino}
                    className={`nav-btn ${page === destino ? 'active' : ''}`}
                    onClick={() => setPage(destino)}
                  >
                    {rotulo}
                  </button>
                ))}
                {grupo.titulo === 'El negocio' && (
                  <div className="nav-ajustes">
                    <button
                      className={`nav-btn ${enAjustes ? 'active' : ''}`}
                      onClick={() => setMenuAbierto(a => !a)}
                    >
                      ⚙️ Ajustes ▾
                    </button>
                    {menuAbierto && (
                      <>
                        {/* Una capa transparente detrás: cualquier clic fuera
                            cierra el menú, sin escuchar en todo el documento. */}
                        <div className="nav-capa" onClick={() => setMenuAbierto(false)} />
                        <div className="nav-menu">
                          {ajustesVisibles.map(([destino, rotulo]) => (
                            <button
                              key={destino}
                              className={page === destino ? 'activo' : ''}
                              onClick={() => { setPage(destino); setMenuAbierto(false); }}
                            >
                              {rotulo}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
                {grupo.titulo === 'El negocio' && (
                  <button
                    className="nav-btn logout-btn"
                    onClick={handleLogout}
                    style={{ marginLeft: 'auto', backgroundColor: '#ef4444' }}
                  >
                    🚪 Salir
                  </button>
                )}
              </div>
            </div>
          ))}
        </nav>
      </header>

      {/* Aviso cuando quedan pocos días. Se muestra dentro de la aplicación
          además del correo, porque no todo el mundo lo revisa a diario. */}
      {cuenta && cuenta.dias_restantes !== null && cuenta.dias_restantes <= 3 && (
        <div style={{
          background: '#fffbeb', borderBottom: '1px solid #fde68a', color: '#92400e',
          padding: '12px 20px', fontSize: '14px', textAlign: 'center'
        }}>
          ⚠️ Tu suscripción vence {cuenta.dias_restantes <= 0
            ? 'hoy'
            : `en ${cuenta.dias_restantes} ${cuenta.dias_restantes === 1 ? 'día' : 'días'}`}.
          {' '}Renuévala para no perder el acceso — tus datos se conservan{' '}
          {cuenta.dias_prorroga} días más aunque se te pase la fecha.
        </div>
      )}

      <AvisoDeVersion />

      <main className="container">
        {/* Va dentro del contenido y no fijo arriba: asi ocupa su sitio sin
            tapar nada, y se ve al entrar sin tener que acordarse de ir a
            Morosos. Se oculta en la propia pantalla de Morosos, donde seria
            decir lo que ya se esta viendo. */}
        {page !== 'delinquents' && (
          <AvisoDeMora onVerMorosos={() => setPage('delinquents')} />
        )}

        {page === 'dashboard' && (
          <Dashboard dashboard={dashboard} clients={clients} onRefresh={fetchDashboard} />
        )}
        {page === 'clients' && (
          <ClientList clients={clients} onRefresh={fetchClients} settings={settings} />
        )}
        {page === 'new-loan' && (
          <NewLoan
            clients={clients}
            settings={settings}
            onSuccess={() => {
              setPage('dashboard');
              fetchDashboard();
              fetchClients();
            }}
          />
        )}
        {page === 'payments' && (
          <Payments clients={clients} onSuccess={() => {
            fetchDashboard();
            fetchClients();
          }} />
        )}
        {page === 'por-cobrar' && <PorCobrar ajustes={settings} />}

        {page === 'estados' && <EstadosDeCuenta ajustes={settings} />}
        {page === 'papelera' && <Papelera onRefresh={fetchClients} />}
        {page === 'acuerdos' && <Acuerdos />}
        {page === 'simulador' && <Simulador />}
        {page === 'importar' && <Importar ajustes={settings} onRefresh={fetchClients} />}

        {page === 'delinquents' && (
          <Delinquents />
        )}
        {page === 'overdue-loans' && (
          <OverdueLoans />
        )}
        {/* Los tres informes viven ahora en una sola pantalla con pestañas.
            Los nombres antiguos se dejan valiendo porque la Guía enlaza a
            ellos: cada uno abre directamente su pestaña. */}
        {['informes', 'reports', 'cobranza', 'overdue-stats'].includes(page) && (
          <Informes inicial={
            page === 'cobranza' ? 'cobranza'
              : page === 'overdue-stats' ? 'vencimientos'
              : 'financieros'} />
        )}
        {page === 'gains' && (
          <GainsReport />
        )}
        {page === 'public-profiles' && (
          <PublicProfiles />
        )}
        {page === 'notifications' && (
          <>
            <AvisoDiario />
            <NotificationManager clients={clients} />
          </>
        )}
        {page === 'expenses' && (
          <Expenses />
        )}
        {page === 'admin' && cuenta && cuenta.es_admin && (
          <AdminEmpresas />
        )}
        {page === 'deuda' && (
          <DeudaCliente clients={clients} onRefresh={fetchClients} ajustes={settings} />
        )}
        {page === 'guia' && (
          <Tutorial onNavigate={(destino) => setPage(destino)} />
        )}
        {page === 'settings' && (
          <Settings settings={settings} onSave={fetchSettings} />
        )}
      </main>
    </div>
  );
}

export default App;
